// customer 報表/查詢頁群：匯出型 5 頁的整頁 body（批次 18，Gitea issue #23）。
// 掛在 PageBehavior.reportBody（ReportPage hooks 前早退；批次 14 先例）。
//
// 各頁形狀（查詢表單＋「匯出」鈕；**legacy 無結果表**——匯出＝$.fileDownload 下載 .xls）：
//   1. high-risk-doctor-report（主治醫師高風險或高技術之醫療行為評核通過醫師名單，
//      /tsghfunc/highRiskDoctorReport）：醫院/部門（皆非必填）＋表單發送期間(起迄)：
//      （startDate/endDate required）。doQuery（raw :1333-1371）：valid() → 日期起訖皆非空
//      且 !isAfterOrSame → `日期選取不正確!`（:1339）→ para={departmentId, startDate,
//      endDateStr: endDate} → fileDownload downloadHighRiskDoctorReportExcel（GET）；
//      failCallback：NoData → `查不到資料!`、else `下載檔案失敗！`。
//   2. high-risk-report（…評核數量統計報表，/tsghfunc/highRiskReport）：doQuery（raw :1326-1364）
//      與上逐字同款，僅端點 downloadHighRiskReportExcel 差。
//   3. high-risk-setting-report（…項目設定結果報表，/tsghfunc/toHighRiskSetReport）：
//      醫院*/科部*（皆 required；級聯 setAllDepartmentElementByOrganization）。
//      doExport（raw :1296-1320）：**無任何驗證** → para={deptId} → fileDownload
//      downloadHighRiskSetReportExcel（GET）；fail 同上。
//   4. service-standard-cfg-report（主治醫師合理醫療服務量設定結果報表，
//      /tsghfunc/serviceStandardCfgReport）：醫院/部門（皆非必填）。doQuery（raw :1316-1343）＝
//      valid() → para={departmentId} → fileDownload downloadServiceStandardCfgExcel（GET）；fail 同。
//   5. service-standard-report（主治醫師合理醫療服務量統計表，/tsghfunc/serviceStandardReport）：
//      醫院/部門＋表單發送期間(起迄)（required）。doQuery（raw :1336-1374）＝valid() → 日期檢查
//      → para={departmentId, startDate, endDateStr} → fileDownload downloadServiceStandardExcel；fail 同。
//   6. tch-satisfaction-survey-report（西醫UGY_學員對科部整體滿意度問卷統計，
//      /tsghrpt/tchSatisfactionSurveyReport/index）：學年度*/醫院*/訓練類別*/部門*/
//      訓練計畫（非必填）＋期間*（startDate/endDate）＋教師* radio（teacherType 1 全部 checked/
//      2 主治醫師/3 住院醫師）＋csrf。ready＝initCommonQueryFields＋**callBackfunc1（raw :1370-1381）
//      ＝jobType option 過濾只剩 西醫UGY/牙醫UGY/中醫PGY**。doExport（raw :1433-1476）：valid() →
//      `Date.parse(start) > Date.parse(end)` → `期間 開始時間 必須 早於 結束.`（:1439）→
//      jobType 選中文字==='中醫PGY' 且 teacherType==='3' → `中醫PGY 沒有 住院醫師資料.`（:1445）→
//      serializeArray＋csrf → fileDownload GET /tsghrpt/tchSatisfactionSurveyReport/doExport；fail 同。
//
// 行號基準＝raw-dom 擷取檔 src/ui/.tmp/ui/raw-dom/legacy/admin/user|train/customer-tsgh-*.html
// （2026-09-26 node 腳本親驗）；controller 端點＝analysis/api-inventory.md（DoctorReportController
// :108/:284/:427/:608、TsghCustomerFuncController :354、TchSatisfactionSurveyRptController :116）。
//
// 雛形策略（批次 14 W1 同款）：
//   - 匯出＝`runReportExport`（`list:<pageId>#export`；endpoint-map 已映射，含 tch 的
//     `list:customer-tsgh-tch-satisfaction-survey-report#export`）＋dataEffect（C5-j）。
//   - 驗證鏉由各頁 chain 負責：required inline（`必填欄位`＝jquery.validate decorator.jsp:262 全域訊息；
//     valid() false → return false 不發 fileDownload，批次 14 F1 複驗）＋日期／特例逐字 alert。
//   - 級聯＝平列池（mock 不逐層 AJAX；批次 15 D2／批次 17 先例，登記）。
//   - csrfToken hidden 欄＝契約欄位（hidden:true），照值帶入 payload（legacy getCSRFTokenValue）。
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { Radio } from '@/components/base/Radio';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { EffectKey } from '@/mocks/endpoint-map';
import { FormWrap } from '@/pages/templates/shared';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  CUSTOMER_TCH_JOB_TYPE_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  TRAINING_PHASE_TARGET_OPTIONS,
  TRAINING_STUDENT_OPTIONS,
} from '@/mocks/resource-rules';
import {
  isAfterOrSame,
  ReportRow,
  runReportExport,
} from '@/pages/report/ReportKit';

/** jquery.validate 全域訊息（decorator.jsp:262；批次 14 同款）。 */
const REQUIRED_TEXT = '必填欄位';

/** 無 inline 錯誤的固定空集（chain 恆 null 的頁用；避免未讀 state）。 */
const NO_ERRORS: Readonly<Record<string, string>> = {};

interface BodyContext {
  v: Readonly<Record<string, string>>;
  set: (key: string, next: string) => void;
  errors: Readonly<Record<string, string>>;
}

/** jquery.validate required inline（valid() false → 欄位下紅字，不發 fileDownload）。 */
function requiredErrorsOf(ctx: BodyContext, keys: readonly string[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of keys) {
    if ((ctx.v[key] ?? '') === '') {
      errors[key] = REQUIRED_TEXT;
    }
  }
  return errors;
}

/** 匯出頁共用外殼（LegacyContractModals＋ContentPanel＋表單＋「匯出」鈕；批次 14 ExportBodyShell 同構）。
 *  鈕不 disabled（批次 17 教訓：C5-h 驅動後同拍點擊不得落空）。 */
function CustomerExportShell(props: {
  pageId: string;
  title: string;
  actionLabel: string;
  actionUiId: string;
  actionEffect: EffectKey;
  values: Record<string, string>;
  onSet: (key: string, next: string) => void;
  errors: Readonly<Record<string, string>>;
  fields: (ctx: BodyContext) => ReactElement;
  /** 驗證鏉（legacy 順序）。回傳：字串＝alert 該文字並中止；''＝inline 錯誤已掛欄位；null＝通過。 */
  chain: (ctx: BodyContext) => Promise<string | null>;
  payload: (v: Readonly<Record<string, string>>) => Record<string, string>;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const ctx: BodyContext = {
    v: props.values,
    set: props.onSet,
    errors: props.errors,
  };
  const run = () => {
    void (async () => {
      const blocking = await props.chain(ctx);
      if (blocking !== null) {
        if (blocking !== '') {
          await dialogs.alert(blocking);
        }
        return; // inline（''）或 alert（文字）都不匯出（批次 14 F1：legacy valid() false → return false）
      }
      await runReportExport({
        pageId: props.pageId,
        filters: props.payload(props.values),
        dataSource,
        dialogs,
      });
    })();
  };
  return (
    <div data-ui-id={props.pageId}>
      <LegacyContractModals pageId={props.pageId} />
      <ContentPanel uiId={`${props.pageId}-panel`} title={props.title}>
        {/* 批次 36（GNL-005-b）：查詢＋匯出鈕包 form 鏡像 legacy（attr 由 LEGACY_FORM_SPECS）。 */}
        <FormWrap pageId={props.pageId}>
          <div data-ui-id={`${props.pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
            {props.fields(ctx)}
          </div>
          <div data-ui-id={`${props.pageId}-actions`} className="mb-3 flex gap-2">
            <Button
              uiId={props.actionUiId}
              variant="primary"
              size="sm"
              type="button"
              dataEffect={props.actionEffect}
              onClick={run}
            >
              {props.actionLabel}
            </Button>
          </div>
        </FormWrap>
      </ContentPanel>
    </div>
  );
}

/** 表單發送期間(起迄)雙欄（high-risk 系／service-standard 系共用；type=date＋required inline）。 */
function PeriodFields(props: {
  pageId: string;
  ctx: BodyContext;
  label: string;
  startName: string;
  endName: string;
}): ReactElement {
  const { pageId, ctx } = props;
  const control = (name: string) => (
    <input
      data-ui-id={`${pageId}-field-${name}`}
      name={name}
      type="date"
      className="rounded border border-border px-2 py-1 text-sm"
      value={ctx.v[name] ?? ''}
      onChange={(event) => {
        ctx.set(name, event.target.value);
      }}
    />
  );
  return (
    <>
      <ReportRow pageId={pageId} name={props.startName} label={props.label} star error={ctx.errors[props.startName]}>
        {control(props.startName)}
      </ReportRow>
      <ReportRow pageId={pageId} name={props.endName} label="~" star error={ctx.errors[props.endName]}>
        {control(props.endName)}
      </ReportRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// 逐頁 body。values 鍵＝legacy name（C5-h）；標題＝選單名稱（routes.generated.ts title）。
// ---------------------------------------------------------------------------

/** 高風險/服務量共通的醫院＋科部（部門）雙 select（非必填頁；醫院*／部門* label 紅星照畫）。 */
function OrgDeptFields(props: {
  pageId: string;
  ctx: BodyContext;
  deptLabel: string;
  deptRequired: boolean;
}): ReactElement {
  const { pageId, ctx } = props;
  return (
    <>
      <ReportRow pageId={pageId} name="organizationId" label="醫院：" star>
        <Select
          uiId={`${pageId}-field-organization-id`}
          name="organizationId"
          value={ctx.v['organizationId'] ?? ''}
          onChange={(next) => {
            ctx.set('organizationId', next);
          }}
          options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
          {...(selectOptionsOverride(pageId, 'organizationId') ? {} : { placeholder: '請選擇' })}
          invalid={ctx.errors['organizationId'] !== undefined}
        />
      </ReportRow>
      <ReportRow pageId={pageId} name="departmentId" label={props.deptLabel} star>
        <Select
          uiId={`${pageId}-field-department-id`}
          name="departmentId"
          value={ctx.v['departmentId'] ?? ''}
          onChange={(next) => {
            ctx.set('departmentId', next);
          }}
          options={selectOptionsOverride(pageId, 'departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
          {...(selectOptionsOverride(pageId, 'departmentId') ? {} : { placeholder: '請選擇' })}
          invalid={ctx.errors['departmentId'] !== undefined}
        />
      </ReportRow>
    </>
  );
}

/** :1 high-risk-doctor-report／:2 high-risk-report 共用 chain（raw :1333-1341 逐字）。 */
async function highRiskChain(ctx: BodyContext): Promise<string | null> {
  const errors = requiredErrorsOf(ctx, ['startDate', 'endDate']);
  if (Object.keys(errors).length > 0) {
    return ''; // inline（呼叫端 setErrors 由 chain 外層負責：見 wrapErrors）
  }
  // :1337-1341 起訖皆非空且 !isAfterOrSame → 日期選取不正確!
  if (!isAfterOrSame(ctx.v['startDate'] ?? '', ctx.v['endDate'] ?? '')) {
    return '日期選取不正確!';
  }
  return null;
}

/** high-risk-doctor-report（主 body；high-risk-report 共用同構，僅 title/endpoint 差）。 */
function HighRiskReportBodyBase(props: {
  pageId: string;
  title: string;
  initial: Record<string, string>;
}): ReactElement {
  const [values, setValues] = useState<Record<string, string>>(props.initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerExportShell
      pageId={props.pageId}
      title={props.title}
      actionLabel="匯出"
      actionUiId={`${props.pageId}-export-btn`}
      actionEffect={`list:${props.pageId}#export`}
      values={values}
      onSet={set}
      errors={errors}
      fields={(ctx) => (
        <>
          <OrgDeptFields pageId={props.pageId} ctx={ctx} deptLabel="部門：" deptRequired={false} />
          <PeriodFields pageId={props.pageId} ctx={ctx} label="表單發送期間(起迄)：" startName="startDate" endName="endDate" />
          <input type="hidden" data-ui-id={`${props.pageId}-field-csrf-token`} name="csrfToken" value={ctx.v['csrfToken'] ?? ''} />
        </>
      )}
      chain={async (ctx) => {
        const result = await highRiskChain(ctx);
        if (result === '') {
          setErrors(requiredErrorsOf(ctx, ['startDate', 'endDate']));
        } else {
          setErrors({});
        }
        return result;
      }}
      // para（raw :1347-1352）＝{csrfToken, departmentId, startDate, endDateStr}；organizationId/
      // endDate 補帶（契約 name 必須進 payload；endDateStr＝legacy 參數名鏡像照帶，D4 登記）。
      payload={(v) => ({
        csrfToken: v['csrfToken'] ?? '',
        organizationId: v['organizationId'] ?? '',
        departmentId: v['departmentId'] ?? '',
        startDate: v['startDate'] ?? '',
        endDate: v['endDate'] ?? '',
        endDateStr: v['endDate'] ?? '',
      })}
    />
  );
}

/** 1. 主治醫師高風險或高技術之醫療行為評核通過醫師名單。 */
export function HighRiskDoctorReportBody({ pageId }: { pageId: string }): ReactElement {
  return (
    <HighRiskReportBodyBase
      pageId={pageId}
      title="主治醫師高風險或高技術之醫療行為評核通過醫師名單"
      initial={{ organizationId: '', departmentId: '', startDate: '', endDate: '', csrfToken: 'csrf-mock' }}
    />
  );
}

/** 2. 主治醫師高風險或高技術之醫療行為評核數量統計報表。 */
export function HighRiskReportBody({ pageId }: { pageId: string }): ReactElement {
  return (
    <HighRiskReportBodyBase
      pageId={pageId}
      title="主治醫師高風險或高技術之醫療行為評核數量統計報表"
      initial={{ organizationId: '', departmentId: '', startDate: '', endDate: '', csrfToken: 'csrf-mock' }}
    />
  );
}

/** 3. 主治醫師高風險及高技術項目設定結果報表（doExport 無驗證；raw :1296-1320）。 */
export function HighRiskSettingReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({ organizationId: 'org-1', departmentId: '', csrfToken: 'csrf-mock' });
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerExportShell
      pageId={pageId}
      title="主治醫師高風險及高技術項目設定結果報表"
      actionLabel="匯出"
      actionUiId={`${pageId}-export-btn`}
      actionEffect={`list:${pageId}#export`}
      values={values}
      onSet={set}
      errors={NO_ERRORS}
      fields={(ctx) => (
        <>
          <ReportRow pageId={pageId} name="organizationId" label="醫院：" star>
            <Select
              uiId={`${pageId}-field-organization-id`}
              name="organizationId"
              value={ctx.v['organizationId'] ?? ''}
              onChange={(next) => {
                ctx.set('organizationId', next);
              }}
              options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
              invalid={ctx.errors['organizationId'] !== undefined}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="departmentId" label="科部：" star>
            <Select
              uiId={`${pageId}-field-department-id`}
              name="departmentId"
              value={ctx.v['departmentId'] ?? ''}
              onChange={(next) => {
                ctx.set('departmentId', next);
              }}
              options={selectOptionsOverride(pageId, 'departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
              {...(selectOptionsOverride(pageId, 'departmentId') ? {} : { placeholder: '請選擇' })}
              invalid={ctx.errors['departmentId'] !== undefined}
            />
          </ReportRow>
          <input type="hidden" data-ui-id={`${pageId}-field-csrf-token`} name="csrfToken" value={ctx.v['csrfToken'] ?? ''} />
        </>
      )}
      // doExport 無任何驗證（raw :1296-1320）；payload＝{deptId}（:1297-1300 參數名鏡像）
      // ＋departmentId（契約 name）/organizationId/csrfToken 補帶（D4 登記，批次 17 先例）。
      chain={async () => null}
      payload={(v) => ({
        deptId: v['departmentId'] ?? '',
        departmentId: v['departmentId'] ?? '',
        organizationId: v['organizationId'] ?? '',
        csrfToken: v['csrfToken'] ?? '',
      })}
    />
  );
}

/** 4. 主治醫師合理醫療服務量設定結果報表（valid()；raw :1316-1343）。 */
export function ServiceStandardCfgReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({ organizationId: '', departmentId: '', csrfToken: 'csrf-mock' });
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerExportShell
      pageId={pageId}
      title="主治醫師合理醫療服務量設定結果報表"
      actionLabel="匯出"
      actionUiId={`${pageId}-export-btn`}
      actionEffect={`list:${pageId}#export`}
      values={values}
      onSet={set}
      errors={NO_ERRORS}
      fields={(ctx) => (
        <>
          <OrgDeptFields pageId={pageId} ctx={ctx} deptLabel="部門：" deptRequired={false} />
          <input type="hidden" data-ui-id={`${pageId}-field-csrf-token`} name="csrfToken" value={ctx.v['csrfToken'] ?? ''} />
        </>
      )}
      // doQuery＝valid()（組織/部門皆非 required → 恆通過）→ fileDownload（raw :1321-1324 para）
      // ＋organizationId 補帶（D4 登記）。
      chain={async () => null}
      payload={(v) => ({
        organizationId: v['organizationId'] ?? '',
        departmentId: v['departmentId'] ?? '',
        csrfToken: v['csrfToken'] ?? '',
      })}
    />
  );
}

/** 5. 主治醫師合理醫療服務量統計表（valid()＋日期檢查；raw :1336-1374）。 */
export function ServiceStandardReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: '',
    departmentId: '',
    startDate: '',
    endDate: '',
    csrfToken: 'csrf-mock',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerExportShell
      pageId={pageId}
      title="主治醫師合理醫療服務量統計表"
      actionLabel="匯出"
      actionUiId={`${pageId}-export-btn`}
      actionEffect={`list:${pageId}#export`}
      values={values}
      onSet={set}
      errors={errors}
      fields={(ctx) => (
        <>
          <OrgDeptFields pageId={pageId} ctx={ctx} deptLabel="部門：" deptRequired={false} />
          <PeriodFields pageId={pageId} ctx={ctx} label="表單發送期間(起迄)：" startName="startDate" endName="endDate" />
          <input type="hidden" data-ui-id={`${pageId}-field-csrf-token`} name="csrfToken" value={ctx.v['csrfToken'] ?? ''} />
        </>
      )}
      chain={async (ctx) => {
        const result = await highRiskChain(ctx);
        if (result === '') {
          setErrors(requiredErrorsOf(ctx, ['startDate', 'endDate']));
        } else {
          setErrors({});
        }
        return result;
      }}
      payload={(v) => ({
        organizationId: v['organizationId'] ?? '',
        departmentId: v['departmentId'] ?? '',
        startDate: v['startDate'] ?? '',
        endDate: v['endDate'] ?? '',
        endDateStr: v['endDate'] ?? '',
        csrfToken: v['csrfToken'] ?? '',
      })}
    />
  );
}

/** 6. 西醫UGY_學員對科部整體滿意度問卷統計（兩段特例驗證＋jobType 三項池；raw :1433-1476）。 */
export function TchSatisfactionSurveyReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '',
    departmentId: '',
    planTargetId: '',
    startDate: '',
    endDate: '',
    teacherType: '1',
    csrfToken: 'csrf-mock',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerExportShell
      pageId={pageId}
      title="西醫UGY_學員對科部整體滿意度問卷統計"
      actionLabel="匯出"
      actionUiId={`${pageId}-export-btn`}
      actionEffect={`list:${pageId}#export`}
      values={values}
      onSet={set}
      errors={errors}
      fields={(ctx) => (
        <>
          <ReportRow pageId={pageId} name="annualId" label="學年度：" star error={ctx.errors['annualId']}>
            <Select
              uiId={`${pageId}-field-annual-id`}
              name="annualId"
              value={ctx.v['annualId'] ?? ''}
              onChange={(next) => {
                ctx.set('annualId', next);
              }}
              options={selectOptionsOverride(pageId, 'annualId') ?? COURSE_CLAZZ_ANNUAL_OPTIONS}
              invalid={ctx.errors['annualId'] !== undefined}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="organizationId" label="醫院：" star error={ctx.errors['organizationId']}>
            <Select
              uiId={`${pageId}-field-organization-id`}
              name="organizationId"
              value={ctx.v['organizationId'] ?? ''}
              onChange={(next) => {
                ctx.set('organizationId', next);
              }}
              options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
              invalid={ctx.errors['organizationId'] !== undefined}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="jobTypeId" label="訓練類別：" star error={ctx.errors['jobTypeId']}>
            <Select
              uiId={`${pageId}-field-job-type-id`}
              name="jobTypeId"
              value={ctx.v['jobTypeId'] ?? ''}
              onChange={(next) => {
                ctx.set('jobTypeId', next);
              }}
              // callBackfunc1（raw :1370-1381）：option 過濾只剩 西醫UGY/牙醫UGY/中醫PGY。
              options={selectOptionsOverride(pageId, 'jobTypeId') ?? CUSTOMER_TCH_JOB_TYPE_OPTIONS}
              {...(selectOptionsOverride(pageId, 'jobTypeId') ? {} : { placeholder: '請選擇' })}
              invalid={ctx.errors['jobTypeId'] !== undefined}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="departmentId" label="部門：" star error={ctx.errors['departmentId']}>
            <Select
              uiId={`${pageId}-field-department-id`}
              name="departmentId"
              value={ctx.v['departmentId'] ?? ''}
              onChange={(next) => {
                ctx.set('departmentId', next);
              }}
              options={selectOptionsOverride(pageId, 'departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
              {...(selectOptionsOverride(pageId, 'departmentId') ? {} : { placeholder: '請選擇' })}
              invalid={ctx.errors['departmentId'] !== undefined}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="planTargetId" label="訓練計畫：">
            <Select
              uiId={`${pageId}-field-plan-target-id`}
              name="planTargetId"
              value={ctx.v['planTargetId'] ?? ''}
              onChange={(next) => {
                ctx.set('planTargetId', next);
              }}
              options={selectOptionsOverride(pageId, 'planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS}
              {...(selectOptionsOverride(pageId, 'planTargetId') ? {} : { placeholder: '請選擇' })}
            />
          </ReportRow>
          <PeriodFields pageId={pageId} ctx={ctx} label="期間：" startName="startDate" endName="endDate" />
          <ReportRow pageId={pageId} name="teacherType" label="教師：" star>
            {/* radio 三顆（raw :1325-1327；批次 14 isShowScore 同款逐顆）。 */}
            <Radio
              uiId={`${pageId}-field-teacher-type-1`}
              name="teacherType"
              value="1"
              checked={(ctx.v['teacherType'] ?? '1') === '1'}
              label="全部"
              onChange={(checked) => {
                if (checked) {
                  ctx.set('teacherType', '1');
                }
              }}
            />
            <Radio
              uiId={`${pageId}-field-teacher-type-2`}
              name="teacherType"
              value="2"
              checked={ctx.v['teacherType'] === '2'}
              label="主治醫師"
              onChange={(checked) => {
                if (checked) {
                  ctx.set('teacherType', '2');
                }
              }}
            />
            <Radio
              uiId={`${pageId}-field-teacher-type-3`}
              name="teacherType"
              value="3"
              checked={ctx.v['teacherType'] === '3'}
              label="住院醫師"
              onChange={(checked) => {
                if (checked) {
                  ctx.set('teacherType', '3');
                }
              }}
            />
          </ReportRow>
          <input type="hidden" data-ui-id={`${pageId}-field-csrf-token`} name="csrfToken" value={ctx.v['csrfToken'] ?? ''} />
        </>
      )}
      chain={async (ctx) => {
        // valid()（raw :1434-1436）：required 欄＝契約 req:true 的 7 欄（csrf hidden 恆有值）。
        const errors = requiredErrorsOf(ctx, [
          'annualId',
          'organizationId',
          'jobTypeId',
          'departmentId',
          'startDate',
          'endDate',
        ]);
        if (Object.keys(errors).length > 0) {
          setErrors(errors);
          return '';
        }
        setErrors({});
        // :1438-1441 Date.parse(start) > Date.parse(end) → 期間 開始時間 必須 早於 結束.
        const start = Date.parse(ctx.v['startDate'] ?? '');
        const end = Date.parse(ctx.v['endDate'] ?? '');
        if (!Number.isNaN(start) && !Number.isNaN(end) && start > end) {
          return '期間 開始時間 必須 早於 結束.';
        }
        // :1443-1447 jobType 選中文字==='中醫PGY' 且 teacherType==='3' → 中醫PGY 沒有 住院醫師資料.
        const jobTypeLabel =
          CUSTOMER_TCH_JOB_TYPE_OPTIONS.find((o) => o.value === (ctx.v['jobTypeId'] ?? ''))?.label ?? '';
        if (jobTypeLabel === '中醫PGY' && (ctx.v['teacherType'] ?? '') === '3') {
          return '中醫PGY 沒有 住院醫師資料.';
        }
        return null;
      }}
      // para＝serializeArray＋csrf（raw :1455-1456）：named 欄位全帶。
      payload={(v) => ({ ...v })}
    />
  );
}

// ---------------------------------------------------------------------------
// 批次 18 S2：查詢型 2 頁（case-report／tsgh-student-course-report）——
// 查詢鈕 $.post → 結果表（批次 14 W2 useStatQuery 同款：list 真打＋種子專屬鍵過濾）。
// ---------------------------------------------------------------------------

/** 查詢型共用的 hook：list(filters) → 只渲染帶 statKey 的列；失敗 alert(desc)。 */
function useCustomerQuery(pageId: string, statKey: string) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  const load = (filters: Record<string, string>) => {
    void (async () => {
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters });
        setRows(result.rows.filter((row) => row[statKey] !== undefined));
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : '查詢失敗');
      } finally {
        setBusy(false);
      }
    })();
  };
  return { rows, busy, load };
}

/** 訓練計畫/階段部門空值檢查（caseReport.jsp :1457-1465 鏡像；isEmpty 或 <=0）。 */
function isUnselected(value: string | undefined): boolean {
  if (value === undefined || value === '') {
    return true;
  }
  const num = Number.parseInt(value, 10);
  return Number.isNaN(num) ? false : num <= 0;
}

/** 訓練計畫/階段名稱 select 共用的欄位列（label 親驗 caseReport.jsp :1263-1314）。 */
function CustomerSelectRow(props: {
  pageId: string;
  name: string;
  label: string;
  star?: boolean | undefined;
  value: string;
  onChange: (next: string) => void;
  options: readonly { value: string; label: string }[];
  placeholder?: string | undefined;
  invalid?: boolean | undefined;
}): ReactElement {
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數
  // （規格 §7.1；本元件是 customer 報表頁群共用的 select 列——在此一處接線，
  // TrainQueryFields／core-curriculum／course-record 全部 CustomerSelectRow 一次覆蓋；
  // override 命中含空陣列即移除 placeholder 空選項）。
  const override = selectOptionsOverride(props.pageId, props.name);
  return (
    <ReportRow pageId={props.pageId} name={props.name} label={props.label} star={props.star} error={props.invalid === true ? '必填欄位' : undefined}>
      <Select
        uiId={`${props.pageId}-field-${props.name}`}
        name={props.name}
        value={props.value}
        onChange={props.onChange}
        options={override ?? props.options}
        {...(props.placeholder === undefined || override ? {} : { placeholder: props.placeholder })}
        invalid={props.invalid === true}
      />
    </ReportRow>
  );
}

/** case-report／student-course 共用的 7+2 欄查詢表單（label 親驗 caseReport.jsp :1263-1314；
 *  級聯平列池＝mock 自由值，legacy 逐層 AJAX 填充，D2 登記）。 */
function TrainQueryFields(props: { pageId: string; ctx: BodyContext }): ReactElement {
  const { pageId, ctx } = props;
  const selectOf = (name: string, label: string, options: readonly { value: string; label: string }[], placeholder?: string) => (
    <CustomerSelectRow
      pageId={pageId}
      name={name}
      label={label}
      star
      value={ctx.v[name] ?? ''}
      onChange={(next) => {
        ctx.set(name, next);
      }}
      options={options}
      {...(placeholder === undefined ? {} : { placeholder })}
      invalid={ctx.errors[name] !== undefined}
    />
  );
  return (
    <>
      {selectOf('organizationId', '醫院：', ACCOUNT_ORGANIZATION_OPTIONS)}
      {selectOf('jobTypeId', '訓練類別：', ACCOUNT_JOB_TYPE_OPTIONS, '請選擇')}
      {selectOf('departmentId', '部門：', ACCOUNT_DEPARTMENT_OPTIONS, '請選擇')}
      {selectOf('annualId', '學年度：', COURSE_CLAZZ_ANNUAL_OPTIONS)}
      {selectOf('planTargetId', '訓練計畫：', COURSE_CLAZZ_PLAN_TARGET_OPTIONS, '請選擇')}
      {selectOf('phaseDepartmentId', '訓練階段部門：', ACCOUNT_DEPARTMENT_OPTIONS, '請選擇')}
      {selectOf('phaseTargetId', '訓練階段名稱：', TRAINING_PHASE_TARGET_OPTIONS, '請選擇')}
      <ReportRow pageId={pageId} name="searchStudentCode" label="學員：">
        <TextInput
          uiId={`${pageId}-field-search-student-code`}
          name="searchStudentCode"
          value={ctx.v['searchStudentCode'] ?? ''}
          onChange={(next) => {
            ctx.set('searchStudentCode', next);
          }}
          placeholder="(請輸入學員姓名或學員編號)"
        />
      </ReportRow>
      <ReportRow pageId={pageId} name="studentId" label="">
        <Select
          uiId={`${pageId}-field-student-id`}
          name="studentId"
          value={ctx.v['studentId'] ?? ''}
          onChange={(next) => {
            ctx.set('studentId', next);
          }}
          options={selectOptionsOverride(pageId, 'studentId') ?? TRAINING_STUDENT_OPTIONS}
          {...(selectOptionsOverride(pageId, 'studentId') ? {} : { placeholder: '請選擇' })}
        />
      </ReportRow>
    </>
  );
}

/** doQuery 共用驗證鏉（caseReport.jsp :1457-1465／tsghStudentCourseReport.jsp :1461-1469 同款）。 */
function trainQueryChain(ctx: BodyContext): Promise<string | null> {
  // planTargetId 空/<=0 → 未選擇訓練計畫!；phaseDepartmentId 空/<=0 → 未輸入訓練階段部門!
  if (isUnselected(ctx.v['planTargetId'])) {
    return Promise.resolve('未選擇訓練計畫!');
  }
  if (isUnselected(ctx.v['phaseDepartmentId'])) {
    return Promise.resolve('未輸入訓練階段部門!');
  }
  return Promise.resolve(null);
}

/** 查詢型 body 共用殼（LegacyContractModals＋ProcessingOverlay＋panel＋查詢區＋查詢鈕＋結果表；
 *  鈕不 disabled＝批次 17 教訓；查詢 dataEffect＝list:<pageId>，C5-h 送出自報＝query-btn）。 */
function CustomerQueryShell(props: {
  pageId: string;
  title: string;
  fields: (ctx: BodyContext) => ReactElement;
  values: Record<string, string>;
  onSet: (key: string, next: string) => void;
  errors: Readonly<Record<string, string>>;
  chain: (ctx: BodyContext) => Promise<string | null>;
  onPass: (filters: Record<string, string>) => void;
  columns: readonly TableColumn[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
  renderCell?: ((row: Row, columnKey: string) => React.ReactNode) | undefined;
  busy: boolean;
}): ReactElement {
  const dialogs = useDialogs();
  const ctx: BodyContext = { v: props.values, set: props.onSet, errors: props.errors };
  const run = () => {
    void (async () => {
      const blocking = await props.chain(ctx);
      if (blocking !== null) {
        if (blocking !== '') {
          await dialogs.alert(blocking);
        }
        return;
      }
      props.onPass({ ...props.values });
    })();
  };
  return (
    <div data-ui-id={props.pageId}>
      <LegacyContractModals pageId={props.pageId} />
      <ProcessingOverlay uiId={`${props.pageId}-processing`} open={props.busy} />
      <ContentPanel uiId={`${props.pageId}-panel`} title={props.title} headingLevel={panelHeadingLevelFor(props.pageId)}>
        {/* 批次 36（GNL-005-b）：查詢＋查詢鈕包 form 鏡像 legacy（表格在 form 外＝legacy 同形）。 */}
        <FormWrap pageId={props.pageId}>
          <div data-ui-id={`${props.pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
            {props.fields(ctx)}
          </div>
          <div data-ui-id={`${props.pageId}-actions`} className="mb-3 flex gap-2">
            <Button
              uiId={`${props.pageId}-query-btn`}
              variant="primary"
              size="sm"
              type="button"
              dataEffect={`list:${props.pageId}` as EffectKey}
              onClick={run}
            >
              查詢
            </Button>
          </div>
        </FormWrap>
        <Table
          uiId={`${props.pageId}-result-table`}
          columns={props.columns}
          rows={props.rows.map((row) => ({ ...row }))}
          rowKey={props.rowKey}
          {...(props.renderCell === undefined ? {} : { renderCell: props.renderCell })}
        />
      </ContentPanel>
    </div>
  );
}

/** 案例報告統計（reportBody；doQuery :1456-1485＋表 6 欄 raw :1341 表頭＋render :1371-1388）。 */
export function CaseReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: 'org-1',
    jobTypeId: '',
    departmentId: '',
    annualId: '115',
    planTargetId: '',
    phaseDepartmentId: '',
    phaseTargetId: '',
    searchStudentCode: '',
    studentId: '',
  });
  // 驗證鏉為純 alert（無 inline 欄位錯誤；:1457-1465）。
  const { rows, busy, load } = useCustomerQuery(pageId, 'minQty');
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerQueryShell
      pageId={pageId}
      title="案例報告統計"
      fields={(ctx) => <TrainQueryFields pageId={pageId} ctx={ctx} />}
      values={values}
      onSet={set}
      errors={NO_ERRORS}
      chain={trainQueryChain}
      onPass={(filters) => {
        load(filters);
      }}
      // 表頭六欄逐字（legacy-table-headers.generated :206-213；raw :1341）。
      columns={[
        { key: 'studentId', header: '員編/學號', align: 'center' },
        { key: 'studentName', header: '學員姓名', align: 'center' },
        { key: 'formTargetTypeName', header: '案例', align: 'center' },
        { key: 'minQty', header: '本院規定實例數', align: 'center' },
        { key: 'doneQty', header: '總完成實例數', align: 'center' },
        { key: 'pass', header: '通過狀態', align: 'center' },
      ]}
      rows={rows}
      rowKey={(row) => String(row['employeeId'] ?? '')}
      renderCell={(row, columnKey) => {
        // :1371-1373 第一欄 render＝employeeId+'/'+schoolId。
        if (columnKey === 'studentId') {
          return `${String(row['employeeId'] ?? '')}/${String(row['schoolId'] ?? '')}`;
        }
        const value = row[columnKey];
        return value === null || value === undefined ? '' : String(value);
      }}
      busy={busy}
    />
  );
}

// ---------------------------------------------------------------------------
// 批次 18 S3：查詢型複雜 2 頁（core-curriculum-report／course-record-report）——
// 動態表頭＋modal 明細群（targetInfo/courseDetail＋tms/tmsDetail/course）。
// ---------------------------------------------------------------------------

/** core-curriculum 型別（種子 targets／tsghDetailList／studentCourseList；C5-h 專屬鍵＝targets）。 */
interface CoreTarget {
  name: string;
  ratio: string;
  sumCaseQty: string;
  caseQty: string;
  tsghDetailList: ReadonlyArray<Row>;
}

/** 查詢 hook 變體：失敗走 showInfoDialog（coreCurriculum.jsp :1539 照實＝info 非 alert）。
 *  S3 body 直接自寫 catch；此處僅保留型別共用。 */

export function StudentCourseReportBody({ pageId }: { pageId: string }): ReactElement {
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: 'org-1',
    jobTypeId: '',
    departmentId: '',
    annualId: '115',
    planTargetId: '',
    phaseDepartmentId: '',
    phaseTargetId: '',
    searchStudentCode: '',
    studentId: '',
  });
  // 驗證鏉為純 alert（無 inline 欄位錯誤；:1461-1469）。
  const { rows, busy, load } = useCustomerQuery(pageId, 'govMinQty');
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  return (
    <CustomerQueryShell
      pageId={pageId}
      title="修課紀錄查詢"
      fields={(ctx) => <TrainQueryFields pageId={pageId} ctx={ctx} />}
      values={values}
      onSet={set}
      errors={NO_ERRORS}
      chain={trainQueryChain}
      onPass={(filters) => {
        load(filters);
      }}
      // 表頭七欄逐字（legacy-table-headers.generated；jobType 前綴欄＝課程內容）。
      columns={[
        { key: 'studentId', header: '員編/學號', align: 'center' },
        { key: 'studentName', header: '學員姓名', align: 'center' },
        { key: 'typeName', header: '課程內容', align: 'center' },
        { key: 'govHours', header: '醫策會規定時數', align: 'center' },
        { key: 'specifiedTime', header: '本院規定時數', align: 'center' },
        { key: 'qty', header: '修課時數', align: 'center' },
        { key: 'totalPer', header: '完成度', align: 'center' },
      ]}
      rows={rows}
      rowKey={(row) => String(row['employeeId'] ?? '')}
      renderCell={(row, columnKey) => {
        // :1371-1373 第一欄＝employeeId+'/'+schoolId。
        if (columnKey === 'studentId') {
          return `${String(row['employeeId'] ?? '')}/${String(row['schoolId'] ?? '')}`;
        }
        // :1377-1380 課程內容＝label-primary span（jobType 選中文字）＋typeName。
        if (columnKey === 'typeName') {
          return (
            <>
              <span className="label label-primary">{String(row['jobTypeLabel'] ?? '')}</span>{' '}
              {String(row['typeName'] ?? '')}
            </>
          );
        }
        // :1381-1383 醫策會規定時數＝govMinQty 至 govMaxQty 小時。
        if (columnKey === 'govHours') {
          return `${String(row['govMinQty'] ?? '')} 至 ${String(row['govMaxQty'] ?? '')} 小時`;
        }
        // :1390-1392 完成度＝totalPer% (qty/specifiedTime)。
        if (columnKey === 'totalPer') {
          return `${String(row['totalPer'] ?? '')}% (${String(row['qty'] ?? '')}/${String(row['specifiedTime'] ?? '')})`;
        }
        const value = row[columnKey];
        return value === null || value === undefined ? '' : String(value);
      }}
      busy={busy}
    />
  );
}

// ---------------------------------------------------------------------------
// 9. 核心課程查詢（core-curriculum-report；resultType radio＋動態表頭＋雙 modal）。
// ---------------------------------------------------------------------------

/** 種子列的 targets 專屬鍵（fixtures 通用列沒有）。 */
function coreTargetsOf(row: Row): readonly CoreTarget[] {
  const targets = row['targets'];
  return Array.isArray(targets) ? (targets as readonly CoreTarget[]) : [];
}

export function CoreCurriculumReportBody({ pageId }: { pageId: string }): ReactElement {
  const dialogs = useDialogs();
  const dataSource = useDataSource();
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: 'org-1',
    jobTypeId: '',
    departmentId: '',
    annualId: '115',
    planTargetId: '',
    groupId: '',
    planStartDate: '',
    planEndDate: '',
    searchStudentCode: '',
    studentId: '',
    resultType: 'plan',
  });
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  // targetInfoModal／courseDetailModal 狀態（showTargetInfo/showCourseDetailModal 鏡像）。
  const [targetInfo, setTargetInfo] = useState<
    | { plan: string; school: string; student: string; target: CoreTarget }
    | undefined
  >();
  const [courseDetail, setCourseDetail] = useState<
    | { item: string; list: ReadonlyArray<Row> }
    | undefined
  >();
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  const run = () => {
    void (async () => {
      // doQuery（:1512-1521）：type=plan→未選擇訓練計畫!；type=month→未輸入訓練期間!。
      const type = values['resultType'] ?? 'plan';
      if (type === 'plan' && isUnselected(values['planTargetId'])) {
        await dialogs.alert('未選擇訓練計畫!');
        return;
      }
      if (
        type === 'month' &&
        ((values['planStartDate'] ?? '') === '' || (values['planEndDate'] ?? '') === '')
      ) {
        await dialogs.alert('未輸入訓練期間!');
        return;
      }
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters: { ...values } });
        setRows(result.rows.filter((row) => row['targets'] !== undefined));
      } catch (cause: unknown) {
        // :1539 失敗走 showInfoDialog（info 非 alert＝照實）。
        await dialogs.info(cause instanceof Error ? cause.message : '查詢失敗');
      } finally {
        setBusy(false);
      }
    })();
  };
  const type = values['resultType'] ?? 'plan';
  // genFormTableHead（:1549-1564）：訓練計畫/員編學號/學員姓名/訓練區間|月份＋targetList[].name
  // （targetList＝全查詢結果的 target 清單＝所有列 targets 的有序聯集，同名合併；
  //  列內 stu.targetList 對位，name=='---'→'-'）。
  const targets: readonly { name: string }[] = (() => {
    const seen: { name: string }[] = [];
    for (const row of rows) {
      for (const target of coreTargetsOf(row)) {
        if (target.name !== '---' && !seen.some((entry) => entry.name === target.name)) {
          seen.push({ name: target.name });
        }
      }
    }
    return seen;
  })();
  const columns: readonly TableColumn[] = [
    { key: 'planTargetName', header: '訓練計畫', align: 'center' },
    { key: 'studentId', header: '員編/學號', align: 'center' },
    { key: 'studentName', header: '學員姓名', align: 'center' },
    { key: type === 'plan' ? 'ddtPlan' : 'ddtMonth', header: type === 'plan' ? '訓練區間' : '訓練月份', align: 'center' },
    ...targets.map((target, index) => ({
      key: `target_${String(index)}`,
      header: target.name,
      align: 'center' as const,
    })),
  ];
  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
      <ContentPanel uiId={`${pageId}-panel`} title="核心課程查詢" headingLevel={panelHeadingLevelFor(pageId)}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
          <CustomerSelectRow pageId={pageId} name="annualId" label="學年度：" star value={values['annualId'] ?? ''} onChange={(next) => { set('annualId', next); }} options={COURSE_CLAZZ_ANNUAL_OPTIONS} invalid={false} />
          <CustomerSelectRow pageId={pageId} name="organizationId" label="醫院：" star value={values['organizationId'] ?? ''} onChange={(next) => { set('organizationId', next); }} options={ACCOUNT_ORGANIZATION_OPTIONS} invalid={false} />
          <CustomerSelectRow pageId={pageId} name="jobTypeId" label="訓練類別：" star value={values['jobTypeId'] ?? ''} onChange={(next) => { set('jobTypeId', next); }} options={ACCOUNT_JOB_TYPE_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="departmentId" label="部門：" star value={values['departmentId'] ?? ''} onChange={(next) => { set('departmentId', next); }} options={ACCOUNT_DEPARTMENT_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="planTargetId" label="訓練計畫：" star value={values['planTargetId'] ?? ''} onChange={(next) => { set('planTargetId', next); }} options={COURSE_CLAZZ_PLAN_TARGET_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="groupId" label="組別：" star value={values['groupId'] ?? ''} onChange={(next) => { set('groupId', next); }} options={TRAINING_PHASE_TARGET_OPTIONS} placeholder="請選擇" invalid={false} />
          {/* 訓練期間（planStartDate/planEndDate readonly＋data-startdate；月份型雙欄）。 */}
          <ReportRow pageId={pageId} name="plan-start-date" label="訓練期間：">
            <input
              data-ui-id={`${pageId}-field-plan-start-date`}
              name="planStartDate"
              type="date"
              readOnly
              className="rounded border border-border px-2 py-1 text-sm"
              value={values['planStartDate'] ?? ''}
              onChange={(event) => {
                set('planStartDate', event.target.value);
              }}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="plan-end-date" label="~">
            <input
              data-ui-id={`${pageId}-field-plan-end-date`}
              name="planEndDate"
              type="date"
              readOnly
              className="rounded border border-border px-2 py-1 text-sm"
              value={values['planEndDate'] ?? ''}
              onChange={(event) => {
                set('planEndDate', event.target.value);
              }}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="searchStudentCode" label="學員：">
            <TextInput
              uiId={`${pageId}-field-search-student-code`}
              name="searchStudentCode"
              value={values['searchStudentCode'] ?? ''}
              onChange={(next) => {
                set('searchStudentCode', next);
              }}
              placeholder="(請輸入學員姓名或學員編號)"
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="studentId" label="">
            <Select
              uiId={`${pageId}-field-student-id`}
              name="studentId"
              value={values['studentId'] ?? ''}
              onChange={(next) => {
                set('studentId', next);
              }}
              options={selectOptionsOverride(pageId, 'studentId') ?? TRAINING_STUDENT_OPTIONS}
              {...(selectOptionsOverride(pageId, 'studentId') ? {} : { placeholder: '請選擇' })}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="resultType" label="統計方式：">
            {/* radio 兩顆（raw :1342-1343：plan 依訓練計畫 checked／month 依訓練月份）。 */}
            <Radio
              uiId={`${pageId}-field-result-type-plan`}
              name="resultType"
              value="plan"
              checked={(values['resultType'] ?? 'plan') === 'plan'}
              label="依訓練計畫"
              onChange={(checked) => {
                if (checked) {
                  set('resultType', 'plan');
                }
              }}
            />
            <Radio
              uiId={`${pageId}-field-result-type-month`}
              name="resultType"
              value="month"
              checked={values['resultType'] === 'month'}
              label="依訓練月份"
              onChange={(checked) => {
                if (checked) {
                  set('resultType', 'month');
                }
              }}
            />
          </ReportRow>
        </div>
        <div data-ui-id={`${pageId}-actions`} className="mb-3 flex gap-2">
          <Button
            uiId={`${pageId}-query-btn`}
            variant="primary"
            size="sm"
            type="button"
            dataEffect={`list:${pageId}` as EffectKey}
            onClick={run}
          >
            查詢
          </Button>
        </div>
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows.map((row) => ({ ...row }))}
          rowKey={(row) => String(row['employeeId'] ?? '')}
          renderCell={(row, columnKey) => {
            // :1571-1577 員編學號合併／訓練區間（startDateString~endDateString）或月份。
            if (columnKey === 'studentId') {
              return `${String(row['employeeId'] ?? '')}/${String(row['schoolId'] ?? '')}`;
            }
            if (columnKey === 'ddtPlan') {
              return `${String(row['startDateString'] ?? '')}~${String(row['endDateString'] ?? '')}`;
            }
            if (columnKey === 'ddtMonth') {
              return String(row['startMonthString'] ?? '');
            }
            if (columnKey.startsWith('target_')) {
              const index = Number.parseInt(columnKey.slice('target_'.length), 10);
              const target = coreTargetsOf(row)[index];
              // :1579-1585 name=='---' → '-'；否則 ratio% 連結（showTargetInfo）。
              if (target === undefined || target.name === '---') {
                return '-';
              }
              return (
                <button
                  type="button"
                  className={LINK_TEXT_CLASS}
                  data-ui-id={`${pageId}-result-table-ratio-${String(row['employeeId'] ?? '')}-${String(index)}`}
                  onClick={() => {
                    setTargetInfo({
                      plan: String(row['planTargetName'] ?? ''),
                      school: `${String(row['employeeId'] ?? '')}/${String(row['schoolId'] ?? '')}`,
                      student: String(row['studentName'] ?? ''),
                      target,
                    });
                  }}
                >
                  {target.ratio}%
                </button>
              );
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </ContentPanel>

      {/* ── targetInfoModal（title &nbsp;照實；header 資訊＋targetInfoTable 六欄） ── */}
      {targetInfo === undefined ? null : (
        <ModalFrame
          uiId={`${pageId}-target-info-modal`}
          open
          title="&nbsp;"
          size="lg"
          onClose={() => {
            setTargetInfo(undefined);
          }}
          footer={
            <Button uiId={`${pageId}-target-info-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => { setTargetInfo(undefined); }}>
              關閉
            </Button>
          }
        >
          <div data-ui-id={`${pageId}-target-info-modal-body`} className="space-y-3">
            <div>
              {targetInfo.target.name}／總案例數：{targetInfo.target.sumCaseQty}／案例數：
              {targetInfo.target.caseQty}／比率：{targetInfo.target.ratio}%
            </div>
            <Table
              uiId={`${pageId}-target-info-table`}
              columns={[
                { key: 'plan', header: '訓練計畫', align: 'center' },
                { key: 'school', header: '員編/學號', align: 'center' },
                { key: 'student', header: '學員姓名', align: 'center' },
                { key: 'studyItemName', header: '核心課程', align: 'center' },
                { key: 'doneQty', header: '執行次數', align: 'center' },
                { key: 'caseQty', header: '應完成次數', align: 'center' },
              ]}
              rows={(targetInfo.target.tsghDetailList as ReadonlyArray<Row>).map((entry) => ({
                ...entry,
                plan: targetInfo.plan,
                school: targetInfo.school,
                student: targetInfo.student,
              }))}
              rowKey={(entry) => String(entry['studyItemName'] ?? '')}
              renderCell={(entry, columnKey) => {
                // doneQty==0 → '0'；否則連結 showCourseDetailModal（studyItemName＋studentCourseList）。
                if (columnKey === 'doneQty') {
                  const doneQty = String(entry['doneQty'] ?? '0');
                  if (doneQty === '0') {
                    return '0';
                  }
                  return (
                    <button
                      type="button"
                      className={LINK_TEXT_CLASS}
                      data-ui-id={`${pageId}-target-info-done-${String(
                        (targetInfo.target.tsghDetailList as ReadonlyArray<Row>).findIndex(
                          (candidate) => String(candidate['studyItemName'] ?? '') === String(entry['studyItemName'] ?? ''),
                        ),
                      )}`}
                      onClick={() => {
                        setCourseDetail({
                          item: String(entry['studyItemName'] ?? ''),
                          list: (entry['studentCourseList'] ?? []) as ReadonlyArray<Row>,
                        });
                      }}
                    >
                      {doneQty}
                    </button>
                  );
                }
                const value = entry[columnKey];
                return value === null || value === undefined ? '' : String(value);
              }}
            />
          </div>
        </ModalFrame>
      )}

      {/* ── courseDetailModal（title &nbsp;照實；courseDetailTable 五欄） ── */}
      {courseDetail === undefined ? null : (
        <ModalFrame
          uiId={`${pageId}-course-detail-modal`}
          open
          title="&nbsp;"
          size="lg"
          onClose={() => {
            setCourseDetail(undefined);
          }}
          footer={
            <Button uiId={`${pageId}-course-detail-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => { setCourseDetail(undefined); }}>
              關閉
            </Button>
          }
        >
          <div data-ui-id={`${pageId}-course-detail-modal-body`} className="space-y-3">
            <div>
              {targetInfo?.target.name ?? ''}／{courseDetail.item}
            </div>
            <Table
              uiId={`${pageId}-course-detail-table`}
              columns={[
                { key: 'plan', header: '訓練計畫', align: 'center' },
                { key: 'school', header: '員編/學號', align: 'center' },
                { key: 'student', header: '學員姓名', align: 'center' },
                { key: 'learningTime', header: '學習日期', align: 'center' },
                { key: 'recordId', header: '病歷號碼', align: 'center' },
              ]}
              rows={courseDetail.list.map((entry) => ({
                ...entry,
                plan: targetInfo?.plan ?? '',
                school: targetInfo?.school ?? '',
                student: targetInfo?.student ?? '',
              }))}
              rowKey={(entry) => String(entry['recordId'] ?? '')}
            />
          </div>
        </ModalFrame>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 10. 修課紀錄查詢（course-record-report；11 欄＋固定 9 欄表＋msg 提示＋三 modal）。
// ---------------------------------------------------------------------------

interface TmsRecord {
  coursetypeid: string;
  courseid: string;
  coursename: string;
  coursedate: string;
  hour: string;
  status: string;
  score: string;
  internal: string;
  isCFD: string;
  method: string;
  assessment: string;
  resource: string;
  research: string;
  summary: string;
  holisticHours: string;
}

const TMS_STATUS_TEXT: Record<string, string> = {
  '0': '未完成',
  '1': '通過',
  '2': '未通過',
};

export function CourseRecordReportBody({ pageId }: { pageId: string }): ReactElement {
  const dialogs = useDialogs();
  const dataSource = useDataSource();
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: 'org-1',
    jobTypeId: '',
    departmentId: '',
    annualId: '115',
    planTargetId: '',
    phaseDepartmentId: '',
    phaseTargetId: '',
    planStartDate: '',
    planEndDate: '',
    searchStudentCode: '',
    studentId: '',
  });
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  // 三 modal 狀態（showCourseModal/showTmsModal/showTmsDetailModal 鏡像）。
  const [courseModalRow, setCourseModalRow] = useState<Row | undefined>();
  const [tmsModalRow, setTmsModalRow] = useState<Row | undefined>();
  const [tmsDetail, setTmsDetail] = useState<{ studentName: string; type: string; entry: TmsRecord } | undefined>();
  const set = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };
  const run = () => {
    void (async () => {
      // doQuery（:1476-1489）：未選擇訓練計畫!→未輸入訓練階段部門!（planStart/End 檢核被註解＝照實不驗）。
      if (isUnselected(values['planTargetId'])) {
        await dialogs.alert('未選擇訓練計畫!');
        return;
      }
      if (isUnselected(values['phaseDepartmentId'])) {
        await dialogs.alert('未輸入訓練階段部門!');
        return;
      }
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters: { ...values } });
        const stat = result.rows.filter((row) => row['myQty'] !== undefined);
        setRows(stat);
        // :1502-1505 msg!='' → showAlertDialog(msg)（後端提示，例：TMS 同步中）。
        const msg = stat[0] === undefined ? '' : String(stat[0]['msg'] ?? '');
        if (msg !== '') {
          await dialogs.alert(msg);
        }
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : '查詢失敗');
      } finally {
        setBusy(false);
      }
    })();
  };
  // genFormTableHead（:1521-1537）固定 9 欄。
  const columns: readonly TableColumn[] = [
    { key: 'studentId', header: '員編/學號', align: 'center' },
    { key: 'studentName', header: '學員姓名', align: 'center' },
    { key: 'typeName', header: '課程類別', align: 'center' },
    { key: 'tmsTypeName', header: 'TMS課程類別', align: 'center' },
    { key: 'specifiedTime', header: '規定時數', align: 'center' },
    { key: 'myQty', header: '修課時數', align: 'center' },
    { key: 'tmsQty', header: 'TMS時數', align: 'center' },
    { key: 'totalQty', header: '總計', align: 'center' },
    { key: 'totalPer', header: '完成度', align: 'center' },
  ];
  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
      <ContentPanel uiId={`${pageId}-panel`} title="修課紀錄查詢" headingLevel={panelHeadingLevelFor(pageId)}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
          <CustomerSelectRow pageId={pageId} name="organizationId" label="醫院：" star value={values['organizationId'] ?? ''} onChange={(next) => { set('organizationId', next); }} options={ACCOUNT_ORGANIZATION_OPTIONS} invalid={false} />
          <CustomerSelectRow pageId={pageId} name="jobTypeId" label="訓練類別：" star value={values['jobTypeId'] ?? ''} onChange={(next) => { set('jobTypeId', next); }} options={ACCOUNT_JOB_TYPE_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="departmentId" label="部門：" star value={values['departmentId'] ?? ''} onChange={(next) => { set('departmentId', next); }} options={ACCOUNT_DEPARTMENT_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="annualId" label="學年度：" star value={values['annualId'] ?? ''} onChange={(next) => { set('annualId', next); }} options={COURSE_CLAZZ_ANNUAL_OPTIONS} invalid={false} />
          <CustomerSelectRow pageId={pageId} name="planTargetId" label="訓練計畫：" star value={values['planTargetId'] ?? ''} onChange={(next) => { set('planTargetId', next); }} options={COURSE_CLAZZ_PLAN_TARGET_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="phaseDepartmentId" label="訓練階段部門：" star value={values['phaseDepartmentId'] ?? ''} onChange={(next) => { set('phaseDepartmentId', next); }} options={ACCOUNT_DEPARTMENT_OPTIONS} placeholder="請選擇" invalid={false} />
          <CustomerSelectRow pageId={pageId} name="phaseTargetId" label="訓練階段名稱：" star value={values['phaseTargetId'] ?? ''} onChange={(next) => { set('phaseTargetId', next); }} options={TRAINING_PHASE_TARGET_OPTIONS} placeholder="請選擇" invalid={false} />
          {/* 訓練期間（planStartDate/planEndDate readonly；檢核被註解＝照實不驗）。 */}
          <ReportRow pageId={pageId} name="plan-start-date" label="訓練期間：">
            <input data-ui-id={`${pageId}-field-plan-start-date`} name="planStartDate" type="date" readOnly className="rounded border border-border px-2 py-1 text-sm" value={values['planStartDate'] ?? ''} onChange={(event) => { set('planStartDate', event.target.value); }} />
          </ReportRow>
          <ReportRow pageId={pageId} name="plan-end-date" label="~">
            <input data-ui-id={`${pageId}-field-plan-end-date`} name="planEndDate" type="date" readOnly className="rounded border border-border px-2 py-1 text-sm" value={values['planEndDate'] ?? ''} onChange={(event) => { set('planEndDate', event.target.value); }} />
          </ReportRow>
          <ReportRow pageId={pageId} name="searchStudentCode" label="學員：">
            <TextInput uiId={`${pageId}-field-search-student-code`} name="searchStudentCode" value={values['searchStudentCode'] ?? ''} onChange={(next) => { set('searchStudentCode', next); }} placeholder="(請輸入學員姓名或學員編號)" />
          </ReportRow>
          <ReportRow pageId={pageId} name="studentId" label="">
            <Select uiId={`${pageId}-field-student-id`} name="studentId" value={values['studentId'] ?? ''} onChange={(next) => { set('studentId', next); }} options={selectOptionsOverride(pageId, 'studentId') ?? TRAINING_STUDENT_OPTIONS} {...(selectOptionsOverride(pageId, 'studentId') ? {} : { placeholder: '請選擇' })} />
          </ReportRow>
        </div>
        <div data-ui-id={`${pageId}-actions`} className="mb-3 flex gap-2">
          <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" dataEffect={`list:${pageId}` as EffectKey} onClick={run}>
            查詢
          </Button>
        </div>
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows.map((row) => ({ ...row }))}
          rowKey={(row) => String(row['employeeId'] ?? '')}
          renderCell={(row, columnKey) => {
            // :1544-1547 員編學號合併。
            if (columnKey === 'studentId') {
              return `${String(row['employeeId'] ?? '')}/${String(row['schoolId'] ?? '')}`;
            }
            // :1553 修課時數 myQty → showCourseModal(index)。
            if (columnKey === 'myQty') {
              return (
                <button type="button" className={LINK_TEXT_CLASS} data-ui-id={`${pageId}-course-qty-${String(row['employeeId'] ?? '')}`} onClick={() => { setCourseModalRow(row); }}>
                  {String(row['myQty'] ?? '')}
                </button>
              );
            }
            // :1555 TMS時數 tmsQty → showTmsModal(index)。
            if (columnKey === 'tmsQty') {
              return (
                <button type="button" className={LINK_TEXT_CLASS} data-ui-id={`${pageId}-tms-qty-${String(row['employeeId'] ?? '')}`} onClick={() => { setTmsModalRow(row); }}>
                  {String(row['tmsQty'] ?? '')}
                </button>
              );
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </ContentPanel>

      {/* ── courseModal（E-learning 課程明細表；courseTable 六欄，只列 attendance==1） ── */}
      {courseModalRow === undefined ? null : (
        <ModalFrame
          uiId={`${pageId}-course-modal`}
          open
          title="E-learning 課程明細表"
          size="lg"
          onClose={() => { setCourseModalRow(undefined); }}
          footer={
            <Button uiId={`${pageId}-course-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => { setCourseModalRow(undefined); }}>
              關閉
            </Button>
          }
        >
          <div data-ui-id={`${pageId}-course-modal-body`}>
            <Table
              uiId={`${pageId}-course-table`}
              columns={[
                { key: 'type', header: '課程類別', align: 'center' },
                { key: 'code', header: '課程代碼', align: 'center' },
                { key: 'name', header: '課程名稱', align: 'center' },
                { key: 'classDate', header: '課程日期', align: 'center' },
                { key: 'qty', header: '時數', align: 'center' },
                { key: 'score', header: '上課表現', align: 'center' },
              ]}
              rows={((courseModalRow['classInCourseList'] ?? []) as ReadonlyArray<Row>)
                .filter((entry) => String(entry['attendance'] ?? '') === '1')
                .map((entry) => ({ ...entry, type: String(courseModalRow['typeName'] ?? '') }))}
              rowKey={(entry) => String(entry['code'] ?? '')}
            />
          </div>
        </ModalFrame>
      )}

      {/* ── tmsModal（title &nbsp;照實；tmsTable 六欄；課程編號連結→tmsDetailModal） ── */}
      {tmsModalRow === undefined ? null : (
        <ModalFrame
          uiId={`${pageId}-tms-modal`}
          open
          title="&nbsp;"
          size="lg"
          onClose={() => { setTmsModalRow(undefined); }}
          footer={
            <Button uiId={`${pageId}-tms-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => { setTmsModalRow(undefined); }}>
              關閉
            </Button>
          }
        >
          <div data-ui-id={`${pageId}-tms-modal-body`}>
            <Table
              uiId={`${pageId}-tms-table`}
              columns={[
                { key: 'type', header: '課程類別', align: 'center' },
                { key: 'courseid', header: '課程編號', align: 'center' },
                { key: 'coursename', header: '課程名稱', align: 'center' },
                { key: 'coursedate', header: '受訓日期', align: 'center' },
                { key: 'hour', header: '時數', align: 'center' },
                { key: 'status', header: '通過狀態', align: 'center' },
              ]}
              rows={((tmsModalRow['tmsCourseRecordList'] ?? []) as ReadonlyArray<TmsRecord>).map((entry) => ({
                ...entry,
                type: String((tmsModalRow['tmsCourseTypeIdMap'] as Record<string, string> | undefined)?.[entry.coursetypeid] ?? ''),
                status: TMS_STATUS_TEXT[entry.status] ?? '-',
              }))}
              rowKey={(entry) => String(entry['courseid'] ?? '')}
              renderCell={(entry, columnKey) => {
                // :1601-1602 課程編號連結 → showTmsDetailModal(studentName, type, index)。
                if (columnKey === 'courseid') {
                  const record = ((tmsModalRow['tmsCourseRecordList'] ?? []) as ReadonlyArray<TmsRecord>).find(
                    (candidate) => String(candidate['courseid'] ?? '') === String(entry['courseid'] ?? ''),
                  );
                  if (record === undefined) {
                    return '-';
                  }
                  return (
                    <button
                      type="button"
                      className={LINK_TEXT_CLASS}
                      data-ui-id={`${pageId}-tms-courseid-${String(record['courseid'] ?? '')}`}
                      onClick={() => {
                        setTmsDetail({
                          studentName: String(tmsModalRow['studentName'] ?? ''),
                          type: String((tmsModalRow['tmsCourseTypeIdMap'] as Record<string, string> | undefined)?.[record.coursetypeid] ?? ''),
                          entry: record,
                        });
                      }}
                    >
                      {String(record['courseid'] ?? '-')}
                    </button>
                  );
                }
                const value = entry[columnKey];
                return value === null || value === undefined ? '' : String(value);
              }}
            />
          </div>
        </ModalFrame>
      )}

      {/* ── tmsDetailModal（title &nbsp;照實；dl 明細 16 項 :1622-1650） ── */}
      {tmsDetail === undefined ? null : (
        <ModalFrame
          uiId={`${pageId}-tms-detail-modal`}
          open
          title="&nbsp;"
          size="lg"
          onClose={() => { setTmsDetail(undefined); }}
          footer={
            <Button uiId={`${pageId}-tms-detail-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => { setTmsDetail(undefined); }}>
              關閉
            </Button>
          }
        >
          <div data-ui-id={`${pageId}-tms-detail-modal-body`} className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {([
              ['studentName', tmsDetail.studentName],
              ['coursetype', tmsDetail.type],
              ['courseid', tmsDetail.entry.courseid || '-'],
              ['coursename', tmsDetail.entry.coursename || '-'],
              ['internal', tmsDetail.entry.internal === '' ? '-' : tmsDetail.entry.internal === '1' ? '內訓' : '外訓'],
              ['hour', tmsDetail.entry.hour || '-'],
              ['score', tmsDetail.entry.score || '-'],
              ['coursedate', tmsDetail.entry.coursedate || '-'],
              ['status', TMS_STATUS_TEXT[tmsDetail.entry.status] ?? '-'],
              ['isCFD', tmsDetail.entry.isCFD === '' ? '-' : tmsDetail.entry.isCFD === '1' ? '是' : '否'],
              ['method', tmsDetail.entry.method || '-'],
              ['assessment', tmsDetail.entry.assessment || '-'],
              ['resource', tmsDetail.entry.resource || '-'],
              ['research', tmsDetail.entry.research || '-'],
              ['summary', tmsDetail.entry.summary || '-'],
              ['holisticHours', tmsDetail.entry.holisticHours || '-'],
            ] as ReadonlyArray<readonly [string, string]>).map(([key, text]) => (
              <div key={key} data-ui-id={`${pageId}-tms-detail-${key}`}>
                {text}
              </div>
            ))}
          </div>
        </ModalFrame>
      )}
    </div>
  );
}